"use client";

import { useMemo, useSyncExternalStore } from "react";
import Image from "next/image";
import { get_all_categories_query } from "@core/services/categories";
import { useMenusContext } from "@core/helpers/client/contexts/MenusContext";
import { ADS_MENU_KEY } from "@/helpers/ads";
import { normalize_card_number, normalize_instagram_id, normalize_sheba, sheba_digits } from "@core/helpers/client/form-renderer/site_helpers";
import CheckBox from "@/components/ui/CheckBox";
import SelectBoxInput from "@/components/ui/SelectBoxInput";
import TextInput from "@/components/ui/TextInput";
import { RadioGroup, useRadioGroup } from "@/components/ui/RadioButton/RadioGroup";
import { FieldHint, SELECT_BOX_STYLE, reveal_open_menu } from "./siteFields";

// فیلدهای سفارشی قالب ژومنت (custom:<key> در پنل). مدیر هر کدام را می‌تواند در هر فرمی بگذارد، پس همه‌ی فرم‌های ژومنت
// همین‌ها را به core می‌دهند (custom_fields). هر کدام این props را از core می‌گیرد:
// { field, value, onChange, error, disabled, required, id }. مقدار هر فیلد همان چیزی است که قالب در پنل برایش
// نوع گذاشته: رشته برای seller-type / card-number / sheba-number / instagram-id و آرایه برای product-categories / ads-services.

// نرمال‌سازی کارت، شبا و آیدی اینستاگرام از core است (site_helpers). شبا با پیشوند ثابت IR ذخیره می‌شود ولی کاربر فقط ۲۴ رقم
// را وارد می‌کند و IR کنار جعبه نوشته شده است.
const SHEBA_PREFIX = "IR";

const HINT_CLASS = "text-jmt-neutral-500";

// ---- وضعیت فروشنده: دو کارت انتخاب ----

const SELLER_TYPE_OPTIONS = [
	{
		value: "personal",
		title: "فروشنده حقیقی هستم",
		description: "برند متعلق به شخص شماست و با کد ملی فعالیت می‌کنید",
		image: "/images/seller-request/personal.webp",
	},
	{
		value: "business",
		title: "فروشنده حقوقی هستم",
		description: "برند با نام تجاری ثبت شده و با شناسه ملی فعالیت می‌کند",
		image: "/images/seller-request/business.webp",
	},
];

const SellerTypeCard = ({ value, title, description, image }) => {
	const group = useRadioGroup();
	const checked = group?.value === value;
	const select = () => group?.onChange?.(value);

	return (
		<div
			role="radio"
			aria-checked={checked}
			tabIndex={0}
			onClick={select}
			onKeyDown={(event) => {
				if (event.key !== "Enter" && event.key !== " ") return;
				event.preventDefault();
				select();
			}}
			className={`relative flex flex-1 cursor-pointer flex-col rounded-md border bg-jmt-white px-4 pt-2 pb-4
			transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-jmt-primary-600
			${checked
					? "border-jmt-primary-800 ring-1 ring-inset ring-jmt-primary-800"
					: "border-jmt-neutral-200 hover:border-jmt-neutral-300"}`}
		>
			<CheckBox
				checked={checked}
				onChange={select}
				tabIndex={-1}
				aria-hidden="true"
				className="absolute! top-4 right-4 w-auto!"
			/>

			<Image src={image} alt="" width={150} height={150} className="mx-auto size-[150px] object-contain" />

			<p className="mt-1.5 text-base font-bold leading-7 text-jmt-main">{title}</p>
			<p className="mt-1 text-sm leading-6 text-jmt-neutral-500">{description}</p>
		</div>
	);
};

const SellerTypeField = ({ field, value, onChange, error, disabled, id }) => (
	<div id={id} role="radiogroup" aria-label={field.label}>
		<RadioGroup value={value} onChange={disabled ? undefined : onChange} className="gap-4! lg:flex-row">
			{SELLER_TYPE_OPTIONS.map((option) => (
				<SellerTypeCard key={option.value} {...option} />
			))}
		</RadioGroup>
		<FieldHint text={field.hint} />
		{error && <p className="mt-2 text-sm text-jmt-red-600">{error}</p>}
	</div>
);

// ---- فیلدهای متنی با نرمال‌سازی ----

const NormalizedTextField = ({
	field,
	value,
	onChange,
	error,
	disabled,
	required,
	id,
	normalize,
	show,
	end_adornment,
	max_length,
}) => (
	<TextInput
		id={id}
		name={id}
		label={field.label}
		required={required}
		disabled={disabled}
		value={show ? show(value) : value}
		onChange={(event) => onChange(normalize(event.target.value))}
		errors={error ? [error] : undefined}
		// جهت را مدیر در پنل روی فیلد می‌گذارد (field.direction)؛ بدون آن مثل صفحه است
		dir={field.direction}
		input_style={field.direction === "ltr" ? "text-left" : undefined}
		inputMode={end_adornment === "@" ? "text" : "numeric"}
		maxLength={max_length}
		placeholder={field.placeholder}
		description={field.hint}
		descriptionClassName={HINT_CLASS}
		endAdornment={end_adornment}
		autoComplete="off"
	/>
);

const CardNumberField = (props) => (
	<NormalizedTextField {...props} normalize={normalize_card_number} />
);

const ShebaNumberField = (props) => (
	<NormalizedTextField
		{...props}
		show={sheba_digits}
		normalize={normalize_sheba}
		end_adornment={SHEBA_PREFIX}
	/>
);

const InstagramIdField = (props) => (
	<NormalizedTextField {...props} normalize={normalize_instagram_id} end_adornment="@" />
);

// ---- دسته‌بندی‌های محصول: خود فیلد گزینه‌هایش را می‌گیرد ----

const PRODUCT_CATEGORY_TYPE = 3;
const CATEGORIES_MOCKUP = `{
    id
    title
    parent_id
    sort
    active
}`;

// گزینه‌ها یک بار برای کل صفحه گرفته می‌شوند و همه‌ی نمونه‌های فیلد از همین حافظه می‌خوانند (بدون state و effect)
const EMPTY_CATEGORIES = { options: [], loading: true };
let categories_state = EMPTY_CATEGORIES;
let categories_started = false;
const categories_listeners = new Set();

const set_categories_state = (next) => {
	categories_state = next;
	categories_listeners.forEach((listener) => listener());
};

const load_categories = () => {
	if (categories_started) return;
	categories_started = true;
	get_all_categories_query(
		{ filter: { type: PRODUCT_CATEGORY_TYPE }, sort: { field: "sort", operator: "asc" } },
		{ categories_mockup: CATEGORIES_MOCKUP },
	)
		.then((result) => {
			if (!result?.status) throw new Error("categories");
			const options = (result?.data || [])
				.filter((category) => category?.id && !category?.parent_id && category?.active !== false)
				.sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0))
				.map((category) => ({ value: String(category.id), label: category.title }));
			set_categories_state({ options, loading: false });
		})
		.catch(() => {
			// دفعه‌ی بعد که فیلد نمایش داده شد دوباره تلاش می‌شود
			categories_started = false;
			set_categories_state({ options: [], loading: false });
		});
};

const subscribe_categories = (listener) => {
	categories_listeners.add(listener);
	load_categories();
	return () => categories_listeners.delete(listener);
};

const useProductCategoryOptions = () =>
	useSyncExternalStore(subscribe_categories, () => categories_state, () => EMPTY_CATEGORIES);

const ProductCategoriesField = ({ field, value, onChange, error, disabled, required, id }) => {
	const { options, loading } = useProductCategoryOptions();
	const selected = useMemo(() => (Array.isArray(value) ? value.map((item) => item?.value) : []), [value]);

	return (
		<div className="mb-7" onClick={reveal_open_menu}>
		<SelectBoxInput
			id={id}
			name={id}
			label={field.label}
			required={required}
			disabled={disabled || loading}
			multiple
			searchable
			auto_flip
			placeholder={field.placeholder || "انتخاب"}
			options={options}
			value={selected}
			onChange={(event) =>
				onChange(
					(event.target.value || [])
						.map((item) => options.find((option) => option.value === String(item)))
						.filter(Boolean)
						.map((option) => ({ title: option.label, value: option.value })),
				)}
			errors={error ? [error] : undefined}
			input_style={SELECT_BOX_STYLE}
		/>
		<FieldHint text={field.hint} />
		</div>
	);
};

// ---- خدمات ژومنت ادز: گزینه‌ها آیتم‌های منوی خدمات تبلیغات‌اند (منوی سراسری، از MenusContext) ----

const AdsServicesField = ({ field, value, onChange, error, disabled, required, id }) => {
	const { menus } = useMenusContext();
	const items = menus?.[ADS_MENU_KEY];
	const options = useMemo(
		() => (items || []).map((item) => item?.title).filter(Boolean).map((title) => ({ label: title, value: title })),
		[items],
	);
	const selected = useMemo(() => (Array.isArray(value) ? value.map((item) => item?.value) : []), [value]);

	const change = (event) => {
		const next = (event.target.value || [])
			.map((item) => options.find((option) => option.value === item))
			.filter(Boolean)
			.map((option) => ({ title: option.label, value: option.value }));
		// هیچ انتخابی = خالی، تا «اجباری» درست بررسی شود
		onChange(next.length ? next : undefined);
	};

	return (
		<div className="mb-7" onClick={reveal_open_menu}>
			<SelectBoxInput
				id={id}
				name={id}
				label={field.label}
				required={required}
				disabled={disabled}
				multiple
				auto_flip
				placeholder={field.placeholder || "انتخاب"}
				options={options}
				value={selected}
				onChange={change}
				errors={error ? [error] : undefined}
				input_style={SELECT_BOX_STYLE}
			/>
			<FieldHint text={field.hint} />
		</div>
	);
};

// کلید هر کدام همان <key> در custom:<key> است. شیء ثابت می‌ماند تا کامپوننت‌ها با هر رندر از نو ساخته نشوند.
export const CUSTOM_FIELDS = {
	"seller-type": SellerTypeField,
	"card-number": CardNumberField,
	"sheba-number": ShebaNumberField,
	"instagram-id": InstagramIdField,
	"product-categories": ProductCategoriesField,
	"ads-services": AdsServicesField,
};
